import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import { api } from "../lib/api";

export default function Realisations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Tous");

  useEffect(() => {
    api.get("/products?in_realisations=true").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["Tous", ...Array.from(new Set(items.map((p) => p.category)))],
    [items]
  );
  const visible = filter === "Tous" ? items : items.filter((p) => p.category === filter);

  return (
    <div>
      <SEO title="Nos produits — pièces déjà réalisées" path="/produits"
        description="Pièces déjà imaginées et réalisées à la main, gravées au laser ou sublimées : planches, boîtes, porte-clés, tote bags. Disponibles à la commande, expédiées sous 48h." />
      <section className="mx-auto max-w-[1400px] px-5 pt-24 pb-14 md:px-10">
        <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Déjà fait</p>
        <h1 className="max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tight text-ink md:text-8xl">
          <MaskedLines lines={["Nos", "produits."]} />
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink2">
            Des pièces déjà imaginées et réalisées à la main — <strong className="text-ink">gravées au laser ou sublimées</strong>, disponibles à la commande.
            Choisissez votre taille et ajoutez-la au panier.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
        <div className="mb-10 flex flex-wrap gap-2" data-testid="realisations-filters">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`real-filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`shrink-0 border px-5 py-2 text-xs uppercase tracking-widest transition-colors ${
                filter === c ? "border-ink bg-ink text-cream" : "border-line text-ink2 hover:bg-sand"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {visible.length === 0 ? (
          <p className="text-ink3">La galerie sera bientôt remplie de nouvelles créations.</p>
        ) : (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6" data-testid="realisations-grid">
            {visible.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      <Marquee items={["Fait Maison", "Fait à la Main", "Pièce Unique", "Gravure Laser", "Sublimation"]} />

      <section className="mx-auto max-w-[1400px] px-5 py-28 md:px-10 md:py-40">
        <Reveal className="border border-line bg-ink p-10 text-cream md:p-20">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight md:text-6xl">
              Vous préférez créer la vôtre ?
            </h2>
            <Link to="/catalogue" data-testid="realisations-cta"
              className="group flex shrink-0 items-center gap-2 border border-cream px-8 py-4 text-sm uppercase tracking-widest transition-colors hover:bg-cream hover:text-ink active:scale-95">
              Personnaliser un produit
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
